import { Injectable } from '@angular/core';
import { delay, distinctUntilChanged } from 'rxjs/operators';
import { gql } from 'apollo-angular';
import { AccountService } from 'src/app/shared/services/account.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ApiInterfaceService } from './api-interface.service';
import { UtilityService } from './utility.service';
import { BoosterReminderComponent } from '../components/booster-reminder/booster-reminder.component';
import { ProfileBuildingService } from './profile-building.service';
import { ModalDialogService } from './modal-dialog.service';

interface BoosterDetails {
  title: string;
  body: string;
  confirmCta: string;
  closeCta: string;
  confirmCtaLink: string;
}

@Injectable({
  providedIn: 'root',
})
export class CovidModalService {
  constructor(
    private api: ApiInterfaceService,
    private authService: AuthService,
    private accountService: AccountService,
    private utilityService: UtilityService,
    private modalDialogService: ModalDialogService,
    private profileBuildingService: ProfileBuildingService,
  ) {}

  /**
   * Initialize covid modal service to subscribe for user auth subject.
   */
  initializeCovidModalService(): void {
    this.authService.userAuthStatusSubject
      .pipe(
        distinctUntilChanged(),
        delay(500), // This is to give chance to other more important modals to load first.
      )
      .subscribe((isLoggedIn) => {
        if (isLoggedIn && this.accountService.userIs('carer')) {
          this.showBoosterReminderDialog();
        }
      });
  }

  /**
   * Show booster reminder dialog.
   */
  private async showBoosterReminderDialog(): Promise<void> {
    try {
      const boosterDialogDetails: BoosterDetails = await this.getBoosterReminderDetails();
      if (boosterDialogDetails.title && boosterDialogDetails.body) {
        this.modalDialogService
          .addDialogToQueue({
            component: BoosterReminderComponent,
            componentProps: { boosterDialogDetails },
            cssClass: 'fullpage-modal',
          })
          .subscribe(() => {
            this.updateBoosterReminderDetails();
          });
      }
    } catch (e) {
      console.error(this.utilityService.getGraphQlError(e));
    }
  }

  /**
   * Send a request to fetch the booster reminder dialog details.
   */
  private async getBoosterReminderDetails(): Promise<BoosterDetails> {
    try {
      const res = await this.api.gqlQuery(gql`
        {
          workerProfile {
            boosterReminder {
              body
              title
              closeCta
              confirmCta
              confirmCtaLink
            }
          }
        }
      `);
      return res['workerProfile']['boosterReminder'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Update booster details for user.
   */
  private async updateBoosterReminderDetails(): Promise<void> {
    const requestBody = {
      userAttributes: {
        profile: {
          boosterReminderDismissed: true,
          covidModalDismissedAt: new Date(),
        },
      },
    };
    try {
      await this.profileBuildingService.updateProfileBuildingDetails(requestBody);
    } catch (e) {
      console.error(this.utilityService.getGraphQlError(e));
    }
  }
}
